import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'reactstrap';
import ItemsList from '../../ItemsList/ItemsList';
import IntegrationCard from '../IntegrationCard/IntegrationCard';
import {
    INTEGRATION_CATEGORIES,
    get_catalog_integrations,
    get_enabled_integrations,
} from '../integrationCatalog';

const IntegrationsMarketplace = ({
    integrations,
    is_superadmin,
    onActivate,
    onConfigure,
}) => {
    const { t } = useTranslation();
    const [active_category, set_active_category] = useState('all');

    const enabled = get_enabled_integrations(integrations, is_superadmin);
    const catalog = get_catalog_integrations(
        integrations,
        is_superadmin,
        active_category === 'all' ? null : active_category
    );

    const catalog_counts = useMemo(() => {
        const counts = {
            all: get_catalog_integrations(integrations, is_superadmin, null).length,
        };

        INTEGRATION_CATEGORIES.forEach(category => {
            counts[category] = get_catalog_integrations(integrations, is_superadmin, category).length;
        });

        return counts;
    }, [integrations, is_superadmin]);

    const category_items = useMemo(() => {
        const category_label = (category_key, label) => (
            <span className="IntegrationsMarketplace-category-label">
                <span className="IntegrationsMarketplace-category-text">{label}</span>
                <Badge color="secondary" className="IntegrationsMarketplace-category-badge">
                    {catalog_counts[category_key]}
                </Badge>
            </span>
        );

        const items = [
            {
                key: 'all',
                selection: 'all',
                title: category_label('all', t('settings.integrations.categories.all')),
                active: active_category === 'all',
                depth: 0,
            },
        ];

        INTEGRATION_CATEGORIES.forEach(category => {
            items.push({
                key: category,
                selection: category,
                title: category_label(
                    category,
                    t(`settings.integrations.categories.${category}`)
                ),
                active: active_category === category,
                depth: 0,
            });
        });

        return items;
    }, [active_category, catalog_counts, t]);

    return (
        <div className="IntegrationsMarketplace">
            {enabled.length > 0 && (
                <section className="IntegrationsMarketplace-section">
                    <h2 className="IntegrationsMarketplace-section-title">
                        {t('settings.integrations.active_section')}
                    </h2>
                    <div className="IntegrationsMarketplace-grid IntegrationsMarketplace-grid--active">
                        {enabled.map(entry => (
                            <IntegrationCard
                                key={entry.key}
                                integration_key={entry.key}
                                image={entry.image}
                                tags={entry.tags}
                                variant="active"
                                config={entry.config}
                                onConfigure={onConfigure}
                            />
                        ))}
                    </div>
                </section>
            )}

            <section className="IntegrationsMarketplace-section">
                <h2 className="IntegrationsMarketplace-section-title">
                    {t('settings.integrations.catalog_section')}
                </h2>

                <ItemsList
                    horizontal
                    items={category_items}
                    onSelect={(selection) => set_active_category(selection)}
                />

                {catalog.length > 0 ? (
                    <div className="IntegrationsMarketplace-grid IntegrationsMarketplace-grid--catalog">
                        {catalog.map(entry => (
                            <IntegrationCard
                                key={entry.key}
                                integration_key={entry.key}
                                image={entry.image}
                                tags={entry.tags}
                                variant="catalog"
                                onActivate={onActivate}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="IntegrationsMarketplace-empty">
                        {t('settings.integrations.catalog_empty')}
                    </p>
                )}
            </section>
        </div>
    );
};

export default IntegrationsMarketplace;
